import { Box, Flex, Text } from '@chakra-ui/react';
import { numberFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { mapMonthlyUsersChartData } from 'transformers/salesMonthlyUsersTransformer';

const UsersCharts = ({ monthlyUsersData, settings, isSubscription }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (monthlyUsersData && settings) {
      const totalUsersChartData = generateTotalUsersData();
      setChartData(totalUsersChartData);
    }
  }, [monthlyUsersData, settings]);

  const getChartOptions = ({ dates, xAxisCategories, maxTotal }) => {
    return {
      chart: {
        fontFamily: 'DM Sans',
        stacked: true,
        animations: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const total = series[0][dataPointIndex] + series[1][dataPointIndex];
          const series1 = w.config.series[0];
          const series2 = w.config.series[1];
          const title = w.config.dates[dataPointIndex].format('MMM YYYY');

          const items = [
            { label: 'Total', value: numberFormat(total) },
            {
              label: series1.name,
              value: numberFormat(series1.data[dataPointIndex]),
              color: series1.color,
            },
            {
              label: series2.name,
              value: numberFormat(series2.data[dataPointIndex]),
              color: series2.color,
            },
          ];

          return renderToString(<ChartTooltip title={title} items={items} />);
        },
      },
      xaxis: {
        categories: xAxisCategories,
      },
      yaxis: {
        min: 0,
        max: (max) => {
          return maxTotal;
        },
        labels: {
          formatter: (value) => {
            if (value === null) {
              return null;
            }
            return numberFormat(value);
          },
        },
      },
      dates,
    };
  };

  const generateTotalUsersData = () => {
    const mappedData = mapMonthlyUsersChartData(monthlyUsersData, settings);
    if (!mappedData) {
      return null;
    }

    const {
      newUsersData,
      returningUsersData,
      monthlyPayingUsersData,
      annuallyPayingUsersData,
      xAxisCategories,
      dates,
    } = mappedData;

    const maxTotalUsers = newUsersData.reduce((max, item, index) => {
      return Math.max(max, item + returningUsersData[index]);
    }, 0);

    const maxTotalPayingUsers = monthlyPayingUsersData.reduce(
      (max, item, index) => {
        return Math.max(max, item + annuallyPayingUsersData[index]);
      },
      0
    );

    const seriesNewVsReturningUsers = [
      {
        name: 'New Users',
        data: newUsersData,
        type: 'column',
        color: '#775ffc',
      },
      {
        name: 'Returning Users',
        data: returningUsersData,
        type: 'column',
        color: '#84d9fd',
      },
    ];

    const seriesPayingUsers = [
      {
        name: 'Monthly',
        data: monthlyPayingUsersData,
        type: 'column',
        color: '#4318ff',
      },
      {
        name: 'Annually',
        data: annuallyPayingUsersData,
        type: 'column',
        color: '#84d9fd',
      },
    ];

    return {
      newVsReturningUsers: {
        series: seriesNewVsReturningUsers,
        options: getChartOptions({
          dates,
          xAxisCategories,
          maxTotal: maxTotalUsers,
        }),
      },
      payingUsers: {
        series: seriesPayingUsers,
        options: getChartOptions({
          dates,
          xAxisCategories,
          maxTotal: maxTotalPayingUsers,
        }),
      },
    };
  };

  return (
    <>
      {chartData && (
        <Flex>
          <Box w={'50%'}>
            <Text mb={30} fontWeight={800}>
              New & Returning Users
            </Text>
            <Chart
              type="bar"
              height={240}
              series={chartData.newVsReturningUsers.series}
              options={chartData.newVsReturningUsers.options}
            />
          </Box>
          {isSubscription && (
            <Box w={'50%'}>
              <Text mb={30} fontWeight={800}>
                Monthly vs. Annually Paying Users
              </Text>
              <Chart
                type="bar"
                height={240}
                series={chartData.payingUsers.series}
                options={chartData.payingUsers.options}
              />
            </Box>
          )}
        </Flex>
      )}
    </>
  );
};

UsersCharts.propTypes = {
  monthlyUsersData: PropTypes.object,
  settings: PropTypes.object,
  isSubscription: PropTypes.bool,
};

export default UsersCharts;
